import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  Stack,
  Collapse,
  IconButton
} from '@mui/material';
import { ArrowBack, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const ACTION_LABELS = {
  school_created:              'School created',
  school_updated:              'School updated',
  school_status_changed:       'Status changed',
  school_deleted:              'School deleted',
  school_delete_otp_requested: 'Delete OTP requested',
  feature_flag_updated:        'Feature flag toggled',
};

const ACTION_COLORS = {
  school_created:              'success',
  school_updated:              'info',
  school_status_changed:       'warning',
  school_deleted:              'error',
  school_delete_otp_requested: 'warning',
  feature_flag_updated:        'default',
};

const formatTimestamp = (ts) =>
  new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

const schoolLabel = (row) =>
  row.school_name
  ?? row.old_values?.name
  ?? `[deleted — ${row.entity_id?.slice(0, 8)}]`;

const AuditLog = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  // Applied filters (drive the fetch)
  const [appliedFilters, setAppliedFilters] = useState({ school_id: '', action: '', start_date: '', end_date: '' });
  // Draft filters (bound to the inputs, applied on click)
  const [draftFilters, setDraftFilters] = useState({ school_id: '', action: '', start_date: '', end_date: '' });

  const [page, setPage] = useState(0); // 0-indexed for TablePagination
  const rowsPerPage = 50;

  useEffect(() => {
    superAdminAPI.getSchools().then((res) => setSchools(res.data.schools || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getAuditLog({
        ...appliedFilters,
        page: page + 1,
        page_size: rowsPerPage,
      });
      setRows(response.data.data.rows);
      setTotal(response.data.data.total);
    } catch (error) {
      toast.error('Failed to load audit log');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApply = () => {
    setPage(0);
    setAppliedFilters(draftFilters);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={1}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Audit log
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/superadmin/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Record of all administrative actions on the platform.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            select
            label="School"
            value={draftFilters.school_id}
            onChange={(e) => setDraftFilters({ ...draftFilters, school_id: e.target.value })}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All schools</MenuItem>
            {schools.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Action"
            value={draftFilters.action}
            onChange={(e) => setDraftFilters({ ...draftFilters, action: e.target.value })}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All actions</MenuItem>
            {Object.entries(ACTION_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="From date"
            type="date"
            size="small"
            value={draftFilters.start_date}
            onChange={(e) => setDraftFilters({ ...draftFilters, start_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To date"
            type="date"
            size="small"
            value={draftFilters.end_date}
            onChange={(e) => setDraftFilters({ ...draftFilters, end_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={handleApply}>Apply</Button>
        </Stack>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell><strong>Timestamp (IST)</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                  <TableCell><strong>School</strong></TableCell>
                  <TableCell><strong>Performed by</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" py={3}>No audit events found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}>
                            {expandedId === row.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{formatTimestamp(row.created_at)}</TableCell>
                        <TableCell>
                          <Chip label={ACTION_LABELS[row.action] || row.action} color={ACTION_COLORS[row.action] || 'default'} size="small" />
                        </TableCell>
                        <TableCell>{schoolLabel(row)}</TableCell>
                        <TableCell>{row.actor_email || row.actor_name || '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ p: 0, borderBottom: expandedId === row.id ? undefined : 'none' }}>
                          <Collapse in={expandedId === row.id} timeout="auto" unmountOnExit>
                            <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
                              <Box sx={{ flex: 1, minWidth: 220 }}>
                                <Typography variant="caption" color="text.secondary">Before</Typography>
                                <pre style={{ fontSize: 12, background: '#f5f5f5', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
                                  {JSON.stringify(row.old_values, null, 2) || 'null'}
                                </pre>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 220 }}>
                                <Typography variant="caption" color="text.secondary">After</Typography>
                                <pre style={{ fontSize: 12, background: '#f5f5f5', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
                                  {JSON.stringify(row.new_values, null, 2) || 'null'}
                                </pre>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
          />
        </Paper>
      )}
    </Container>
  );
};

export default AuditLog;
